/**
 * Bulk Actions Toolbar Component
 *
 * Sticky bar when Email keys are selected: add/remove role, leftover jobs, Delete.
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/lib/logging';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Loader2, X, Mail, RotateCcw, Wand2 } from 'lucide-react';
import { CORE_ROLES, UserRole, ROLE_METADATA } from './types/rbac';
import {
  bulkAddRoles,
  bulkRemoveRoles,
  bulkDeleteUsers,
  bulkMigrateEmailKeys,
  bulkRestoreEmailKeys,
  bulkSendSignInEmails,
  BulkOperationResult,
} from '@/components/authnz/lib/bulk-operations';
import { isStepUpRequiredFailure } from '@/lib/access/step-up';
import { EnhancedUser } from '@/components/authnz/lib/user-management';
import { isSelfTarget, typedEmailMatches } from '@/lib/admin-lockout-guards';
import { TypedEmailConfirmation } from '@/components/authnz/TypedEmailConfirmation';
import type { EmailKeyUserRow } from '@/lib/users-roles/types';
import {
  eligibleEmailKeys,
  type BulkLeftoverOp,
} from '@/lib/users-roles/bulk-eligibility';

type ConfirmAction =
  | { type: 'addRole'; role: UserRole }
  | { type: 'removeRole'; role: UserRole }
  | { type: 'delete' }
  | { type: BulkLeftoverOp };

interface BulkActionsToolbarProps {
  selectedUsers: Set<string>;
  users: EnhancedUser[];
  rows?: EmailKeyUserRow[];
  matchingCount?: number;
  allMatchingSelected?: boolean;
  offPageSelectedCount?: number;
  onSelectMatching?: () => void;
  onOperationComplete: () => void;
  onClearSelection: () => void;
  onStepUpRequired?: () => void;
}

export function BulkActionsToolbar({
  selectedUsers,
  users,
  rows,
  matchingCount,
  allMatchingSelected: allMatchingSelectedProp,
  offPageSelectedCount = 0,
  onSelectMatching,
  onOperationComplete,
  onClearSelection,
  onStepUpRequired,
}: BulkActionsToolbarProps) {
  const { user: currentUser, refreshServerSession } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [typedEmail, setTypedEmail] = useState('');

  const selectedUserObjects = users.filter((u) => selectedUsers.has(u.userId));
  const selectedCount = selectedUsers.size;
  const selectionIncludesSelf = selectedUserObjects.some((target) =>
    isSelfTarget({
      actorUid: currentUser?.uid,
      actorEmail: currentUser?.email,
      targetUid: target.userId,
      targetEmail: target.email,
    }),
  );
  const requiresTypedSelfConfirm =
    Boolean(confirmAction) &&
    selectionIncludesSelf &&
    (confirmAction?.type === 'delete' ||
      (confirmAction?.type === 'removeRole' && confirmAction.role === UserRole.ADMINISTRATOR));
  const selfEmail = currentUser?.email || '';
  const typedSelfConfirmed = typedEmailMatches(typedEmail, selfEmail);

  const leftoverRows = rows ?? [];
  const migrateN = eligibleEmailKeys(leftoverRows, selectedUsers, 'migrate').length;
  const emailN = eligibleEmailKeys(leftoverRows, selectedUsers, 'send-email').length;
  const restoreN = eligibleEmailKeys(leftoverRows, selectedUsers, 'restore').length;
  const showLeftover = leftoverRows.length > 0;
  const allMatchingSelected =
    allMatchingSelectedProp ??
    (typeof matchingCount === 'number' &&
      matchingCount > 0 &&
      selectedCount >= matchingCount);

  const handleBulkOperation = async (
    operation: () => Promise<BulkOperationResult>,
    successMessage: string,
  ) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const result = await operation();

      if (result.success > 0) {
        toast({
          title: 'Success',
          description: `${successMessage}: ${result.success} user(s) updated`,
        });
      }

      if (result.failed > 0) {
        const stepUpHit = result.errors.some((entry) => isStepUpRequiredFailure(entry.error));
        if (stepUpHit) {
          onStepUpRequired?.();
        }
        toast({
          title: 'Partial Success',
          description: `${result.failed} operation(s) failed.`,
          variant: 'destructive',
        });
        logger.error('Bulk operation errors:', result.errors);
      }

      await refreshServerSession();
      onOperationComplete();
      onClearSelection();
      setConfirmAction(null);
      setTypedEmail('');
    } catch (error) {
      logger.error('Bulk operation error:', error);
      toast({
        title: 'Error',
        description: 'An error occurred during the bulk operation.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async (role: UserRole) => {
    if (!currentUser) return;
    const userIds = Array.from(selectedUsers);
    const userEmails = new Map(selectedUserObjects.map((u) => [u.userId, u.email]));
    await handleBulkOperation(
      () => bulkAddRoles(userIds, role, currentUser.uid, userEmails),
      `Added ${ROLE_METADATA[role].displayName} role`,
    );
  };

  const handleRemoveRole = async (role: UserRole) => {
    const userIds = Array.from(selectedUsers);
    const userEmails = new Map(selectedUserObjects.map((u) => [u.userId, u.email]));
    const uidByUserId = new Map(
      leftoverRows
        .filter((row) => selectedUsers.has(row.emailKey))
        .flatMap((row) => {
          const uid = row.uid ?? row.operationUserId;
          return uid ? [[row.emailKey, uid] as const] : [];
        }),
    );
    await handleBulkOperation(
      () =>
        bulkRemoveRoles(userIds, role, userEmails, {
          confirmSelf: requiresTypedSelfConfirm && typedSelfConfirmed,
          uidByUserId,
        }),
      `Removed ${ROLE_METADATA[role].displayName} role`,
    );
  };

  const handleDelete = async () => {
    if (!currentUser) return;
    await handleBulkOperation(
      () =>
        bulkDeleteUsers(Array.from(selectedUsers), currentUser.uid, {
          confirmSelf: requiresTypedSelfConfirm && typedSelfConfirmed,
        }),
      'Deleted users',
    );
  };

  const handleLeftover = async (op: BulkLeftoverOp) => {
    const keys = eligibleEmailKeys(leftoverRows, selectedUsers, op);
    if (op === 'migrate') {
      await handleBulkOperation(() => bulkMigrateEmailKeys(keys), 'Migrated leftovers');
      return;
    }
    if (op === 'send-email') {
      await handleBulkOperation(() => bulkSendSignInEmails(keys), 'Sent sign-in email');
      return;
    }
    await handleBulkOperation(() => bulkRestoreEmailKeys(keys), 'Restored');
  };

  const openConfirmAction = (action: ConfirmAction) => {
    setTypedEmail('');
    setConfirmAction(action);
  };

  if (selectedCount === 0) {
    return null;
  }

  const leftoverLabel =
    confirmAction?.type === 'migrate'
      ? `Migrate ${migrateN} leftover email${migrateN === 1 ? '' : 's'}? Ineligible selected rows are skipped.`
      : confirmAction?.type === 'send-email'
        ? `Send sign-in email to ${emailN} invited email${emailN === 1 ? '' : 's'}? Ineligible selected rows are skipped.`
        : confirmAction?.type === 'restore'
          ? `Restore ${restoreN} deleted email${restoreN === 1 ? '' : 's'}? Ineligible selected rows are skipped.`
          : null;

  return (
    <>
      <div
        data-testid="bulk-actions-toolbar"
        className="sticky top-0 z-10 rounded-md border border-border bg-card/95 backdrop-blur-sm py-3 px-3"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">
              {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
              {offPageSelectedCount > 0
                ? ` · ${offPageSelectedCount} not on this page`
                : ''}
            </span>
            {onSelectMatching && matchingCount !== undefined && !allMatchingSelected ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                data-testid="bulk-select-all-matching"
                onClick={onSelectMatching}
              >
                Select all {matchingCount} matching
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={onClearSelection} className="h-8">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {showLeftover ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2"
                  disabled={loading || migrateN === 0}
                  data-testid="bulk-migrate"
                  onClick={() => openConfirmAction({ type: 'migrate' })}
                >
                  <Wand2 className="h-4 w-4" />
                  Migrate ({migrateN})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2"
                  disabled={loading || emailN === 0}
                  data-testid="bulk-send-email"
                  onClick={() => openConfirmAction({ type: 'send-email' })}
                >
                  <Mail className="h-4 w-4" />
                  Send email ({emailN})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2"
                  disabled={loading || restoreN === 0}
                  data-testid="bulk-restore"
                  onClick={() => openConfirmAction({ type: 'restore' })}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore ({restoreN})
                </Button>
              </>
            ) : null}
            <Select
              onValueChange={(value) =>
                openConfirmAction({ type: 'addRole', role: value as UserRole })
              }
            >
              <SelectTrigger
                className="h-10 w-[180px]"
                aria-label="Add Role"
                data-testid="bulk-add-role"
              >
                <SelectValue placeholder="Add Role" />
              </SelectTrigger>
              <SelectContent>
                {CORE_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    Add {ROLE_METADATA[role].displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              onValueChange={(value) =>
                openConfirmAction({ type: 'removeRole', role: value as UserRole })
              }
            >
              <SelectTrigger
                className="h-10 w-[180px]"
                aria-label="Remove Role"
                data-testid="bulk-remove-role"
              >
                <SelectValue placeholder="Remove Role" />
              </SelectTrigger>
              <SelectContent>
                {CORE_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    Remove {ROLE_METADATA[role].displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openConfirmAction({ type: 'delete' })}
              disabled={loading}
              className="h-10 gap-2 text-destructive"
              data-testid="bulk-delete"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          </div>
        </div>
      </div>

      {confirmAction && (
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmAction(null);
              setTypedEmail('');
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction.type === 'addRole' && 'Add Role to Users?'}
                {confirmAction.type === 'removeRole' && 'Remove Role from Users?'}
                {confirmAction.type === 'delete' && 'Delete Users?'}
                {confirmAction.type === 'migrate' && 'Migrate leftovers?'}
                {confirmAction.type === 'send-email' && 'Send sign-in emails?'}
                {confirmAction.type === 'restore' && 'Restore deleted emails?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction.type === 'addRole' && (
                  <>
                    Are you sure you want to add{' '}
                    <span className="font-semibold">
                      {ROLE_METADATA[confirmAction.role].displayName}
                    </span>{' '}
                    role to {selectedCount} user{selectedCount !== 1 ? 's' : ''}?
                  </>
                )}
                {confirmAction.type === 'removeRole' && (
                  <>
                    Are you sure you want to remove{' '}
                    <span className="font-semibold">
                      {ROLE_METADATA[confirmAction.role].displayName}
                    </span>{' '}
                    role from {selectedCount} user{selectedCount !== 1 ? 's' : ''}?
                  </>
                )}
                {confirmAction.type === 'delete' && (
                  <>
                    Are you sure you want to delete {selectedCount} user
                    {selectedCount !== 1 ? 's' : ''}? This will remove all role
                    assignments and mark them as deleted. They cannot sign in until
                    an administrator restores access.
                  </>
                )}
                {leftoverLabel}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {requiresTypedSelfConfirm && (
              <TypedEmailConfirmation
                email={selfEmail}
                value={typedEmail}
                onChange={setTypedEmail}
                id="bulk-typed-email-confirm"
              />
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="bulk-confirm"
                onClick={(event) => {
                  event.preventDefault();
                  if (confirmAction.type === 'addRole') {
                    void handleAddRole(confirmAction.role);
                  } else if (confirmAction.type === 'removeRole') {
                    void handleRemoveRole(confirmAction.role);
                  } else if (confirmAction.type === 'delete') {
                    void handleDelete();
                  } else {
                    void handleLeftover(confirmAction.type);
                  }
                }}
                disabled={loading || (requiresTypedSelfConfirm && !typedSelfConfirmed)}
                className={
                  confirmAction.type === 'delete'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : ''
                }
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
